'use client';
import { useState } from 'react';
import { clsx } from 'clsx';
import { FOUNDATION_CLIENT_ID_DEFAULT } from 'components/UI/FoundationPlayer';
import FoundationPlayerClient from 'components/UI/FoundationPlayer/FoundationPlayer.client';
import { Icon } from 'components/UI/Icon';
import { Title, type TitleLink } from 'components/UI/Title';
import { VideoSwiper } from 'components/UI/VideoSwiper';
import type { ThemenweltProps } from 'types/cms';
import type { VideoProps } from 'types/videoTypes';
import { slugify } from 'utils/string';
import styles from './playlist.module.scss';

export type WidgetTeaserVideoPlaylistProps = {
  readonly model?: {
    readonly fields?: {
      readonly headline?: string;
      readonly link?: TitleLink;
    };
    readonly themenwelt?: ThemenweltProps;
  };
  readonly items?: Array<{
    readonly model: VideoProps;
  }>;
  readonly trackingContext?: {
    readonly teaserOffset?: number;
    readonly sectionSlug?: string;
  };
  readonly clientIdentifier?: string;
  readonly initiallyOpen?: boolean;
  readonly showMeta?: boolean;
};

export const WidgetTeaserPlaylist = ({ context }: { context: WidgetTeaserVideoPlaylistProps }) => {
  const { headline, link } = context?.model?.fields ?? {};
  const { initiallyOpen = false, showMeta = true } = context;
  const isClient = typeof window !== 'undefined';
  const rawVideoList = context?.items?.map((item) => item.model) as Array<VideoProps>;
  const teaserOffset = context.trackingContext?.teaserOffset ?? 0;
  const sectionSlug = context.trackingContext?.sectionSlug ?? slugify(headline ?? 'playlist');
  const videoList = rawVideoList.map((video, index) => ({
    ...video,
    teaserId: `${sectionSlug}-teaser${teaserOffset + index + 1}`,
  }));
  const [currentVideoIndex, setCurrentVideoIndex] = useState<number>(initiallyOpen ? 0 : -1);
  const [isAdPlaying, setIsAdPlaying] = useState<boolean>(false);
  const [adClipsLeft, setAdClipsLeft] = useState<number>(0);
  const [videoPlaying, setVideoPlaying] = useState<boolean>(false);
  const [currentStartType, setCurrentStartType] = useState<number>(initiallyOpen ? 0 : 2);
  const [currentAutoplay, setCurrentAutoplay] = useState<boolean>(!initiallyOpen);

  const currentVideo = currentVideoIndex === -1 ? undefined : rawVideoList[currentVideoIndex];

  const handleContentFinished = () => {
    setCurrentStartType(1);
    setCurrentVideoIndex((prev) => {
      const next = prev + 1;
      return next < rawVideoList.length ? next : prev;
    });
  };

  const handleError = () => {
    setCurrentVideoIndex((prev) => {
      const next = prev + 1;
      return next < rawVideoList.length ? next : prev;
    });
  };

  const bgColor = context?.model?.themenwelt?.secondary_color ?? '#020203';

  return (
    videoList?.length > 0 && (
      <div className={styles.root} style={{ backgroundColor: `${bgColor}` }}>
        {!!headline && (
          <Title as="h2" className={styles.title} link={link} showLogo={false}>
            {headline}
          </Title>
        )}
        {currentVideo && isClient && (
          <div
            className={clsx(styles.video, { [styles.initiallyOpen]: initiallyOpen })}
            style={{ aspectRatio: '16 / 9' }}
          >
            <FoundationPlayerClient
              video={currentVideo}
              postRollDisabled
              isAdPlayingListener={setIsAdPlaying}
              adClipsLeftListener={setAdClipsLeft}
              contentFinishedListener={handleContentFinished}
              videoPlayingListener={setVideoPlaying}
              hasErrorListener={handleError}
              autoplay={currentAutoplay}
              startType={currentStartType}
              clientIdentifier={context.clientIdentifier ?? FOUNDATION_CLIENT_ID_DEFAULT}
              embedType="playlist"
            />
          </div>
        )}

        {context.initiallyOpen && !isClient && <div style={{ aspectRatio: '16 / 9' }} />}

        {videoList?.length > 1 && (
          <>
            <VideoSwiper
              onClick={(index: number) => {
                setCurrentStartType(2);
                setCurrentAutoplay(true);
                setCurrentVideoIndex(index);
              }}
              videoList={videoList}
              isAdPlaying={isAdPlaying}
              currentVideoIndex={currentVideoIndex}
              videoPlaying={videoPlaying}
              bgColor={bgColor}
              adClipsLeft={adClipsLeft}
            />

            {showMeta && (
              <div className={styles.meta}>
                <Icon name="playlist" />
                Playlist <span>•</span>
                {videoList.length} Videos
              </div>
            )}
          </>
        )}
      </div>
    )
  );
};
